import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Warning from "pages/profile/common/warning";
import BankDetailsInformation from "./bankDetails/bankDetailsInformation";
import EditBankDetailsInformation from "./bankDetails/editBankDetailsInformation";
import UseBankDetailsInformation from "./bankDetails/useBankDetailsInformation";
import CashExpressInformation from "../common/cashExpressSection/cashExpressInformation";
import EditCashExpressInformation from "../common/cashExpressSection/editCashExpressInformation";
import UseCashExpressInformation from "./cashExpress/useCashExpressInformation";
import PreferredCurrencyInformation from "../common/preferredCurrency/preferredCurrencyInformation";
import EditPreferredCurrency from "../common/preferredCurrency/editPreferredCurrency";
import UsePreferredCurrency from "./preferredCurrency/usePreferredCurrency";
import VatInformation from "../common/vatSection/vatInformation";
import EditVatInformation from "../common/vatSection/editVatInformation";
import UseVatInformation from "./vat/useVatInformation";
import UseUreedInformation from "./ureedSection/useUreedInformation";
import EditUreedInformation from "./ureedSection/editUreedInformation";
import UreedInformation from "./ureedSection/ureedInformation";
import UsePayoneerInformation from "../common/payoneerSection/usePayoneerInformation";
import PayoneerInformation from "../common/payoneerSection/payoneerInformation";
import EditPayoneerInformation from "../common/payoneerSection/editPayoneerInformation";
import { SHOW_VENDOR_PREFERRED_CURRENCY_UI } from "utils/constantValues";

export default function FreelancerFinancialDetails() {
  const [t] = useTranslation();
  const userDetails = useSelector((state) => state.userDetails || {});

  const [isEditPreferredCurrency, setIsEditPreferredCurrency] = useState(false);
  const [isEditVat, setIsEditVat] = useState(false);
  const [isEditBank, setIsEditBank] = useState(false);
  const [isEditUreed, setIsEditUreed] = useState(false);
  const [isEditPayoneer, setIsEditPayoneer] = useState(false);
  const [isEditCash, setIsEditCash] = useState(false);

  const { onEditBankDetails, isBankDetailsLoading } =
    UseBankDetailsInformation();
  const { onEditUreed, isUreedLoading } = UseUreedInformation();
  const { onEditPayoneer, isPayoneerLoading } = UsePayoneerInformation();
  const { onEditCashExpress, isCashExpressLoading } =
    UseCashExpressInformation();
  const { onEditPreferredCurrency, isPreferredCurrencyLoading } =
    UsePreferredCurrency();
  const { onEditVat, isVatLoading } = UseVatInformation();

  return (
    <>
      <div className="flex items-center gap-[12px]">
        <div className="text-[#1D1B22] font-semibold text-[30px]">
          {t("financialInformation")}
        </div>
        {SHOW_VENDOR_PREFERRED_CURRENCY_UI && userDetails?.currency?.code ? (
          <div className="flex py-[4px] px-[8px] justify-center items-center gap-[2px] rounded-[6px] bg-[#E0E6EB] border border-[#B5C8D6]">
            <div className="text-primary text-center font-semibold text-[14px] mt-1">
              {userDetails?.currency?.code}
            </div>
          </div>
        ) : null}
      </div>
      <div className="bg-[#E4E4E7] h-[1px] w-full" />

      <Warning message={t("financialDetailsWarning")} />

      {SHOW_VENDOR_PREFERRED_CURRENCY_UI ? (
        <>
          {/* Preferred Currency */}
          {isEditPreferredCurrency ? (
            <EditPreferredCurrency
              onDiscard={() => setIsEditPreferredCurrency(false)}
              onSave={async (values) => {
                await onEditPreferredCurrency(values);
                setIsEditPreferredCurrency(false);
              }}
              isLoading={isPreferredCurrencyLoading}
            />
          ) : (
            <PreferredCurrencyInformation
              isShowEditIcon
              onEditClick={() => setIsEditPreferredCurrency(true)}
            />
          )}
          <div className="bg-[#E4E4E7] h-[1px] w-full" />
        </>
      ) : null}

      {/* Bank Details */}
      {isEditBank ? (
        <EditBankDetailsInformation
          onDiscard={() => setIsEditBank(false)}
          onSave={async (values) => {
            await onEditBankDetails(values);
            setIsEditBank(false);
          }}
          isLoading={isBankDetailsLoading}
        />
      ) : (
        <BankDetailsInformation
          isShowEditIcon
          onEditClick={() => setIsEditBank(true)}
        />
      )}

      <div className="bg-[#E4E4E7] h-[1px] w-full" />

      {/* Ureed */}
      {isEditUreed ? (
        <EditUreedInformation
          onDiscard={() => setIsEditUreed(false)}
          onSave={async (values) => {
            await onEditUreed(values);
            setIsEditUreed(false);
          }}
          isLoading={isUreedLoading}
        />
      ) : (
        <UreedInformation
          isShowEditIcon
          onEditClick={() => setIsEditUreed(true)}
        />
      )}

      <div className="bg-[#E4E4E7] h-[1px] w-full" />
      {/* Payoneer */}
      {isEditPayoneer ? (
        <EditPayoneerInformation
          onDiscard={() => setIsEditPayoneer(false)}
          onSave={async (values) => {
            await onEditPayoneer(values);
            setIsEditPayoneer(false);
          }}
          isLoading={isPayoneerLoading}
        />
      ) : (
        <PayoneerInformation
          isShowEditIcon
          onEditClick={() => setIsEditPayoneer(true)}
        />
      )}

      <div className="bg-[#E4E4E7] h-[1px] w-full" />

      {/* Cash Express */}
      {isEditCash ? (
        <EditCashExpressInformation
          onDiscard={() => setIsEditCash(false)}
          onSave={async (values) => {
            await onEditCashExpress(values);
            setIsEditCash(false);
          }}
          isLoading={isCashExpressLoading}
        />
      ) : (
        <CashExpressInformation
          isShowEditIcon
          onEditClick={() => setIsEditCash(true)}
        />
      )}

      <div className="bg-[#E4E4E7] h-[1px] w-full" />

      {/* VAT */}
      {isEditVat ? (
        <EditVatInformation
          onDiscard={() => setIsEditVat(false)}
          onSave={async (values) => {
            await onEditVat(values);
            setIsEditVat(false);
          }}
          isLoading={isVatLoading}
        />
      ) : (
        <VatInformation isShowEditIcon onEditClick={() => setIsEditVat(true)} />
      )}
    </>
  );
}
